/**
 * React Query hook for V4 Dashboard Analytics data
 */

import { useQuery } from '@tanstack/react-query'
import { useAppSelector } from 'src/store/hooks'
import { selectGroupsWithDefaults } from 'src/v3/manage/manage.slice'
import httpClient from 'src/utilities/http-client'
import { DashboardQueryParams } from '../types'

/**
 * Analytics chart data format (dynamic keys based on categories)
 */
export interface AnalyticsChartData {
  [key: string]: string | number // licenseType/timeFrame/groupName + category values
}

/**
 * Query key factory for analytics data
 */
export let analyticsQueryKeys = {
  all: ['v4-dashboard', 'analytics'] as const,
  data: (params: DashboardQueryParams) =>
    [...analyticsQueryKeys.all, 'data', params] as const,
}

/**
 * Build analytics URL with auto group resolution
 */
function buildAnalyticsUrlWithGroupResolution(
  params: DashboardQueryParams,
  groups: any[],
): string {
  let searchParams = new URLSearchParams()

  // Required parameters
  searchParams.append('linkedServiceID', params.linkedServiceID)
  searchParams.append('startDate', params.startDate)
  searchParams.append('endDate', params.endDate)

  // Resolve groups to user IDs and combine with existing user selections
  let groupUserIDs = params.groupIDs?.length
    ? params.groupIDs.flatMap((groupID) => {
        let group = groups.find((g) => g.id === groupID)
        return group?.users || []
      })
    : []

  let allUserIDs = [...(params.userIDs || []), ...groupUserIDs]

  // Remove duplicates and send as users parameter (not groups)
  let uniqueUserIDs = Array.from(new Set(allUserIDs))
  if (uniqueUserIDs.length > 0) {
    searchParams.append('users', uniqueUserIDs.join(','))
  }

  // Other optional parameters (no groups since we resolved them to users)
  if (params.categories?.length) {
    searchParams.append('categories', params.categories.join(','))
  }

  if (params.licenseTypes?.length) {
    searchParams.append('licenseTypes', params.licenseTypes.join(','))
  }

  return `/api/manage/dashboard/analytics?${searchParams.toString()}`
}

/**
 * Fetch analytics data from the API
 */
async function fetchAnalyticsData(
  params: DashboardQueryParams,
  groups: any[],
): Promise<AnalyticsChartData[]> {
  let url = buildAnalyticsUrlWithGroupResolution(params, groups)
  let response = await httpClient.get<AnalyticsChartData[]>(url)
  return response.data
}

/**
 * Hook to fetch analytics data with React Query
 */
export function useAnalyticsData(
  params: DashboardQueryParams,
  enabled: boolean = true,
) {
  // Get groups data from Redux to resolve auto groups
  let groups = useAppSelector(
    selectGroupsWithDefaults(params.linkedServiceID || ''),
  )

  let isEnabled =
    enabled &&
    Boolean(params.linkedServiceID && params.startDate && params.endDate)

  return useQuery({
    queryKey: analyticsQueryKeys.data(params),
    queryFn: () => fetchAnalyticsData(params, groups),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
